import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { api, type Client, type Site } from './api';

/**
 * What the shell needs to let you *switch* rather than navigate: every
 * organisation you belong to, and every site in the one you are looking
 * at. The switchers in the sidebar live above the routed page, so this
 * has to be fetched once for the whole shell — and the Organisations and
 * Sites pages then read the same lists rather than fetching them again.
 *
 * `null` means "not loaded yet"; an error is kept separately so a switcher
 * can fall back to the id in the URL instead of showing nothing.
 */
interface DirectoryState {
  /** The organisation in the URL, if any — `/c/:client`. */
  client: string | null;
  clients: Client[] | null;
  clientsError: string | null;
  /** Sites of `client`, or `null` when there is no organisation in the URL. */
  sites: Site[] | null;
  sitesError: string | null;
}

const Ctx = createContext<DirectoryState | null>(null);

export function DirectoryProvider({ children }: { children: ReactNode }) {
  const { client } = useParams();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [sites, setSites] = useState<Site[] | null>(null);
  const [sitesError, setSitesError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<{ clients: Client[] }>('/v1/clients')
      .then((r) => { if (live) setClients(r.data.clients); })
      .catch((e) => { if (live) setClientsError((e as Error).message); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    setSites(null);
    setSitesError(null);
    if (!client) return;
    let live = true;
    api.get<{ sites: Site[] }>(`/v1/clients/${encodeURIComponent(client)}/sites`)
      .then((r) => { if (live) setSites(r.data.sites); })
      .catch((e) => { if (live) setSitesError((e as Error).message); });
    return () => { live = false; };
  }, [client]);

  return (
    <Ctx.Provider value={{ client: client ?? null, clients, clientsError, sites, sitesError }}>
      {children}
    </Ctx.Provider>
  );
}

/** The directory when there is one, null outside the provider: for a page that only uses it to label things. */
export function useOptionalDirectory(): DirectoryState | null {
  return useContext(Ctx);
}

export function useDirectory(): DirectoryState {
  const d = useContext(Ctx);
  if (!d) throw new Error('useDirectory outside <DirectoryProvider>');
  return d;
}
